import { AlertIcon, CheckCircleIcon, ClockIcon, CpuIcon, PersonIcon, ShieldLockIcon, SyncIcon } from "@primer/octicons-react";
import { useChanges, useCi } from "../api/hooks";
import type { AuditEntry, Change, Decision, Evidence, Verdict } from "../api/types";
import { describeStatus, estimateRemaining, isRunning, type Status, type Turn } from "../lib/supervise";
import { cn } from "../lib/utils";
import { CompactStepper } from "./Pipeline";
import { beeStateFor, BeeMascot } from "./BeeMascot";

const TURN: Record<Turn, { label: string; Icon: typeof AlertIcon; box: string; chip: string }> = {
  none: { label: "Nothing needed from you", Icon: SyncIcon, box: "border-border", chip: "bg-neutral-muted text-muted-foreground" },
  you: { label: "Needs you", Icon: PersonIcon, box: "border-warning-emphasis bg-warning-subtle", chip: "bg-warning-emphasis text-on-emphasis" },
  agent: { label: "Agent's turn", Icon: CpuIcon, box: "border-danger-muted bg-danger-subtle", chip: "bg-danger-emphasis text-on-emphasis" },
  admin: { label: "Needs an admin", Icon: ShieldLockIcon, box: "border-danger-muted bg-danger-subtle", chip: "bg-danger-emphasis text-on-emphasis" },
  main: { label: "Waiting on main", Icon: ClockIcon, box: "border-warning-muted bg-warning-subtle", chip: "bg-warning-muted text-foreground" },
  done: { label: "Done", Icon: CheckCircleIcon, box: "border-border", chip: "bg-merged-emphasis text-on-emphasis" },
};

/** Who acts next, as a small pill for lists. */
export function TurnChip({ turn, spin }: { turn: Turn; spin?: boolean }) {
  const t = TURN[turn];
  return (
    <span className={cn("inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold", t.chip)}>
      <t.Icon size={12} className={spin ? "animate-spin" : undefined} /> {t.label}
    </span>
  );
}

/** Who acts next for a server verdict; rejected keeps the client's split into agent and admin. */
const VERDICT_TURN: Partial<Record<Verdict, Turn>> = { landed: "done", will_land: "none", needs_agent: "agent", needs_person: "you", waiting: "none" };

/** The server's decision overrides who acts and says what must happen next. */
function withDecision(s: Status, c: Change, d?: Decision): Status {
  if (!d || d.state !== c.state) return s;
  const turn = d.verdict === "waiting" && d.code === "MAIN_RED" ? "main" : (VERDICT_TURN[d.verdict] ?? s.turn);
  const acts = d.verdict === "needs_agent" || d.verdict === "needs_person" || d.verdict === "waiting";
  return { turn, title: s.title, detail: acts ? `Next: ${d.needs}` : s.detail };
}

/** Primer's Banner for a change: one sentence on where it is, who acts next, then the lifecycle. */
export function StatusBar({ c, run, audit, queuePosition, etaMs, canReview, decision }: { c: Change; run: Evidence[]; audit: AuditEntry[]; queuePosition?: number; etaMs?: number | null; canReview?: boolean; decision?: Decision }) {
  const s = withDecision(describeStatus(c, run, { queuePosition, etaMs, canReview }), c, decision);
  const t = TURN[s.turn];
  const gating = run.filter((e) => e.status !== "skipped");
  const done = gating.filter((e) => !isRunning(e)).length;
  const showProgress = c.state === "ci" && gating.length > 0;
  return (
    <section className={cn("mt-4 overflow-hidden rounded-md border bg-background", t.box)} aria-live="polite">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <BeeMascot state={beeStateFor(c.state)} cell={2} className="max-sm:hidden" />
        <TurnChip turn={s.turn} spin={s.turn === "none" && isActive(c)} />
        <div className="min-w-0 flex-1">
          <p className="m-0 font-semibold wrap-break-word">{s.title}</p>
          {s.detail && <p className="m-0 mt-0.5 text-muted-foreground">{s.detail}</p>}
        </div>
      </div>
      {showProgress && (
        <div className="h-1 bg-neutral-muted" role="progressbar" aria-valuemin={0} aria-valuemax={gating.length} aria-valuenow={done} aria-label="Checks finished">
          <div className="h-full bg-success-emphasis transition-[width] duration-500" style={{ width: `${(done / gating.length) * 100}%` }} />
        </div>
      )}
      <div className="border-t border-border-muted bg-muted px-4 py-2">
        <CompactStepper state={c.state} audit={audit} />
      </div>
    </section>
  );
}

const isActive = (c: Change) => ["created", "ci", "ready", "train", "landing"].includes(c.state);

/** StatusBar with its context: place in the landing queue and CI time left from past runs. */
export function ChangeStatus({ repo, c, run, audit, canReview, decision }: { repo: string; c: Change; run: Evidence[]; audit: AuditEntry[]; canReview?: boolean; decision?: Decision }) {
  const ready = useChanges(repo, "ready", c.state === "ready");
  const ci = useCi(repo);
  const queuePosition = c.state === "ready" && c.ready_at ? (ready.data?.changes ?? []).filter((x) => x.id !== c.id && (x.ready_at ?? Infinity) < c.ready_at!).length + 1 : undefined;
  const etaMs = c.state === "ci" && ci.data ? estimateRemaining(run, ci.data.totals) : null;
  return <StatusBar c={c} run={run} audit={audit} queuePosition={queuePosition} etaMs={etaMs} canReview={canReview} decision={decision} />;
}
